import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Link, Typography, Skeleton } from "@mui/material";

import { ServiceProvider } from "../../api/types/topServiceProviders.types";
import { VerificationIcon } from "../../../../assets/icons/MUI/NetworkIcons";
import { ServiceProviderRatingIcon } from "../../assets/icons/go-hire-icons";
import { useUserDataFormatter } from "../../../User/hooks/useUserDataFormatter";

const ServiceProviderCard = ({ user }: { user: ServiceProvider }) => {
  const { t } = useTranslation("user", { keyPrefix: "userCard" });
  const navigate = useNavigate();

  const {
    fullName,
    firstTwoChr,
    countryName,
    countryMedia,
    isVerified,
    media,
    role,
    profileLink,
    mutualFollowersCount,
    playerPositionShort,
    friendsCount,
  } = useUserDataFormatter(user);

  const { rating } = user.serviceInsights;

  return (
    <Box sx={styles.cardWrapper}>
      <Box sx={styles.avatarWrapper}>
        <Avatar src={media} sx={styles.avatar}>
          {firstTwoChr}
        </Avatar>
        {isVerified && <VerificationIcon sx={styles.verificationIcon} />}
      </Box>
      <Box sx={styles.contentWrapper}>
        <Box sx={styles.nameWrapper}>
          <Link sx={styles.nameLink} onClick={() => navigate(profileLink)}>
            {fullName}
          </Link>
          <img src={countryMedia} height="9px" width="12px" alt={countryName} />
        </Box>

        <Typography sx={styles.roleText}>
          {role}
          {playerPositionShort ? ` . (${playerPositionShort})` : ""}
        </Typography>

        <Typography sx={styles.mutualText}>
          {Number(mutualFollowersCount) > 0 && t("mutualFriends", { count: Number(mutualFollowersCount) })}
        </Typography>
        <Typography sx={styles.mutualText}>
          {Number(friendsCount) > 0 && t("friendWith", { count: Number(friendsCount) })}
        </Typography>
        <Typography sx={styles.ratingWrapper}>
          <ServiceProviderRatingIcon />
          {rating}
        </Typography>
      </Box>
    </Box>
  );
};

const ServiceProviderCardLoader = () => {
  return (
    <Box sx={styles.cardWrapper}>
      <Box sx={styles.avatarWrapper}>
        <Skeleton variant="circular" sx={styles.avatar} />
      </Box>
      <Box sx={styles.contentWrapper}>
        <Box sx={styles.nameWrapper}>
          <Skeleton width={100} height={24} />
          <Skeleton width={12} height={24} />
        </Box>
        <Skeleton width={80} height={20} />
        <Skeleton width={60} height={20} />
      </Box>
    </Box>
  );
};

export { ServiceProviderCard, ServiceProviderCardLoader };

const styles = {
  cardWrapper: {
    width: "170px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "25px",
    padding: "12px",
    borderRadius: "4px",
    background: "#FFF",
    boxShadow: "0px 12px 20px 0px rgba(0, 0, 0, 0.04), 0px 0px 2px 0px rgba(0, 0, 0, 0.06)",
    gridColumn: "span 1",
    marginBottom: "16px",
  } as const,

  avatarWrapper: {
    position: "relative",
    width: "fit-content",
  } as const,

  avatar: {
    width: "70px",
    height: "70px",
  } as const,

  verificationIcon: {
    position: "absolute",
    bottom: "-9px",
    left: "50%",
    transform: "translateX(-50%)",
  } as const,

  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "6px",
  } as const,

  nameWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "3px",
  } as const,

  nameLink: {
    textDecoration: "none",
    maxWidth: "140px",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    cursor: "pointer",
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  } as const,

  roleText: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
    textAlign: "center",
  } as const,

  mutualText: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
  } as const,

  ratingWrapper: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    display: "flex",
    alignItems: "center",
    gap: "2px",
  } as const,
};
